import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useRef, useState } from 'react';
import type { ZoneDef } from '../../../lib/effects/types';
import { smallBtn } from '../fields';

/** Where the map opens when the zone has no place yet (and no other zone to start from). */
const FALLBACK: L.LatLngTuple = [51.5074, -0.1278];

const hasCenter = (z: ZoneDef) =>
  z.shape === 'grid'
    ? z.grid != null
    : z.polar != null && !(z.polar.lat === 0 && z.polar.lng === 0);

/** The zone's shape as Leaflet bounds, for fitting the view. */
function boundsOf(z: ZoneDef, defaultRadiusM: number): L.LatLngBounds | null {
  if (z.shape === 'grid' && z.grid)
    return L.latLngBounds(
      [z.grid.minLat ?? 0, z.grid.minLng ?? 0],
      [z.grid.maxLat ?? 0, z.grid.maxLng ?? 0],
    );
  if (z.shape !== 'grid' && z.polar) {
    const r = z.polar.radiusM || defaultRadiusM;
    return L.latLng(z.polar.lat ?? 0, z.polar.lng ?? 0).toBounds(r * 2);
  }
  return null;
}

/**
 * The Zones page's map (ADR 0045 §4; Leaflet + OpenStreetMap tiles, admin-only): a polar zone is a circle (click to place the centre;
 * radius 0 = the default from effectRules), a grid zone is a box (click two opposite corners). Other zones in the draft show grey.
 * Writes straight back through `onChange`; the numeric fields beside it edit the same values.
 */
export function ZoneMap({
  zone,
  others,
  defaultRadiusM,
  onChange,
}: {
  zone: ZoneDef;
  others: ZoneDef[];
  defaultRadiusM: number;
  onChange: (next: ZoneDef) => void;
}) {
  const host = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  const [corner, setCorner] = useState<L.LatLng | null>(null);
  // The click handler reads the latest props through a ref (Leaflet keeps the first closure).
  const latest = useRef({ zone, onChange, corner });
  useEffect(() => {
    latest.current = { zone, onChange, corner };
  }, [zone, onChange, corner]);

  useEffect(() => {
    if (!host.current || map.current) return;
    const m = L.map(host.current, { zoomControl: true }).setView(FALLBACK, 14);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(m);
    layer.current = L.layerGroup().addTo(m);
    m.on('click', (ev: L.LeafletMouseEvent) => {
      const { zone: z, onChange: set, corner: first } = latest.current;
      const setFirst = setCorner;
      const lat = round(ev.latlng.lat);
      const lng = round(ev.latlng.lng);
      if (z.shape === 'grid') {
        if (!first) {
          setFirst(L.latLng(lat, lng));
          return;
        }
        setFirst(null);
        set({
          ...z,
          grid: {
            minLat: Math.min(first.lat, lat),
            minLng: Math.min(first.lng, lng),
            maxLat: Math.max(first.lat, lat),
            maxLng: Math.max(first.lng, lng),
          },
        });
      } else {
        set({ ...z, polar: { radiusM: z.polar?.radiusM ?? 0, lat, lng } });
      }
    });
    map.current = m;
    return () => {
      m.remove();
      map.current = null;
    };
  }, []);

  // Fit to the zone when a different zone is opened (or the first time it gets a place).
  const fitted = useRef<string | null>(null);
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    const key = `${zone.id ?? ''}:${zone.shape ?? ''}:${hasCenter(zone)}`;
    if (fitted.current === key) return;
    fitted.current = key;
    const b = hasCenter(zone) ? boundsOf(zone, defaultRadiusM) : null;
    const other = others.map((o) => boundsOf(o, defaultRadiusM)).find((x) => x != null);
    if (b) m.fitBounds(b, { maxZoom: 17 });
    else if (other) m.fitBounds(other, { maxZoom: 15 });
  }, [zone, others, defaultRadiusM]);

  // Redraw the shapes.
  useEffect(() => {
    const g = layer.current;
    if (!g) return;
    g.clearLayers();
    const draw = (z: ZoneDef, mine: boolean) => {
      const style = mine
        ? { color: '#0b7fab', weight: 2, fillOpacity: 0.15 }
        : { color: '#888', weight: 1, fillOpacity: 0.05, dashArray: '4' };
      if (z.shape === 'grid' && z.grid) L.rectangle(boundsOf(z, defaultRadiusM)!, style).addTo(g);
      else if (z.shape !== 'grid' && z.polar && hasCenter(z)) {
        const c = L.latLng(z.polar.lat ?? 0, z.polar.lng ?? 0);
        L.circle(c, { ...style, radius: z.polar.radiusM || defaultRadiusM }).addTo(g);
        if (mine) L.circleMarker(c, { radius: 4, color: '#0b7fab', fillOpacity: 1 }).addTo(g);
      }
    };
    for (const o of others) draw(o, false);
    draw(zone, true);
    if (corner) L.circleMarker(corner, { radius: 5, color: '#c0392b', fillOpacity: 1 }).addTo(g);
  }, [zone, others, defaultRadiusM, corner]);

  return (
    <div className="sm:col-span-2">
      <div className="mb-1 flex items-center justify-between text-xs text-bruise">
        <span>
          {zone.shape === 'grid'
            ? corner
              ? 'Click the opposite corner.'
              : 'Click one corner of the box, then the opposite corner.'
            : `Click to place the centre. Radius ${zone.polar?.radiusM || `${defaultRadiusM} (default)`} m.`}
        </span>
        {corner && (
          <button type="button" className={smallBtn} onClick={() => setCorner(null)}>
            Cancel corner
          </button>
        )}
      </div>
      <div ref={host} data-testid="zone-map" className="h-96 w-full rounded border-2 border-ink" />
    </div>
  );
}

const round = (v: number) => Math.round(v * 1e6) / 1e6;
